import { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  Add,
  Button,
  Company,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  Home,
  Input,
  LeftNavButton,
  LeftNavGroup,
  LeftNavHome,
  LeftNavItem,
  LeftNavItemDropdown,
  LeftNavItemDropdownContent,
  LeftNavItemDropdownRadioGroup,
  LeftNavItemDropdownRadioItem,
  LeftNavItemDropdownSeparator,
  LeftNavItemDropdownTrigger,
  LeftNav as LeftNavWrapper,
  Person,
  Search,
  Team,
} from '@mesh.ai/rabbit';
import { QuickActions } from 'Deer/components/QuickActions/QuickActions';
import { routes } from 'Deer/constants';
import { NON_DEER_ROUTES } from 'Deer/constants/nonDeerRoutes';
import { staticRoutes } from 'Deer/constants/routes';
import { getUserAccess, getUserL1Access } from 'Deer/helpers/getUserAccess';
import { useDeerViewId } from 'Deer/hooks/useDeerViewId';
import { useHoverDropdownMenu } from 'Deer/hooks/useHoverDropdownMenu';
import { useL1IntentNavigation } from 'Deer/hooks/useL1IntentNavigation';
import { useNonDeerRouteDetails } from 'Deer/hooks/useNonDeerRouteDetails';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { setGlobalSearchVisible, setL2Visible, useSelectIsGlobalSearchVisible } from 'Deer/slice/DeerNav.slice';
import { useSelectedLoggedInUsersAccesses } from 'Deer/slice/UserAccessesSlice';
import NotificationsWrapper from 'components/Notifications/NotificationsWrapper';
import { NotificationsProvider } from 'components/Notifications/context';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import Fuse from 'fuse.js';
import { useDispatch } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { setShowViewsPanel } from 'reducers/viewsSlice';
import styled from 'styled-components';
import { checkEnableCompanySupport, checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { Settings } from 'assets/assetsV1/iconComponents';
import CustomLogo from 'assets/assetsV1/icons/netchexLogo.svg';

import {
  sendClickedOnCompany,
  sendClickedOnDirectTeam,
  sendClickedOnHome,
  sendClickedOnMe,
  sendClickedOnQuickAdd,
  sendClickedOnSearch,
  sendClickedOnSkipTeam,
  sendClickedOnTeams,
} from 'constants/mixpanelHelper';

import { Button as ButtonV1 } from 'uiToolkitV1/Button';
import { TextLabel } from 'uiToolkitV1/Typography';

import { getApplicableL2CompanyModules } from '../secondary/Company/CompanyModules';
import { getApplicableL2MyDataModules } from '../secondary/ModuleSection';
import {
  getApplicableL2MyTeamModules,
  getApplicableL2ViewModules,
  getViewRoute,
} from '../secondary/MyTeam/MyTeamModules';
import { EmptyViews } from './EmptyViews';
import { ProfileMenu } from './ProfileMenu';

const ManageIcon = (props) => <Settings {...props} boxSize="24px" />;

const CustomLogoWrapper = styled.img`
  height: 24px;
  width: 24px;
`;

export const PrimaryNav = () => {
  const { isExact: isHome } = useRouteMatch(routes.home);
  const dispatch = useDispatch();
  const { moduleName } = useOrganisationSettings();
  const [teamMenuStatus, setTeamMenuStatus] = useHoverDropdownMenu();
  const [createMenuStatus, setCreateMenuStatus] = useHoverDropdownMenu();

  const { l1Module } = useRouteToNavLevels();
  const { l1Module: l1ModuleNonDeer, label: nonDeerRouteLabel } = useNonDeerRouteDetails() ?? {};

  const l1ModuleSelected = l1Module ?? l1ModuleNonDeer;

  let { teamType } = useRouteToMyTeamLevels();

  const isMyData = l1ModuleSelected === 'myData';
  const isTeam = l1ModuleSelected === 'team';
  const isCompany = l1ModuleSelected === 'company';

  let { viewId } = useDeerViewId();
  // -----------------------------------------------------------------
  // specifically for non deer result page navigated from views
  const { source, subSource } = useParsedQuery();
  // special condition in case of non deer route: review result
  if (l1ModuleNonDeer && nonDeerRouteLabel === NON_DEER_ROUTES.reviewsResultPage.label) {
    if (source === 'view') {
      viewId = subSource;
    } else {
      teamType = source;
    }
  }
  // -----------------------------------------------------------------

  const { cohorts } = useSelectedLoggedInUsersAccesses();

  const l1Accesses = getUserL1Access();
  const userAccesses = getUserAccess();

  // fuzzy search
  const [searchTerm, setSearchTerm] = useState('');

  const fuse = new Fuse(cohorts, {
    keys: ['name'],
    threshold: 0.4,
  });

  const results = fuse.search(searchTerm);

  const handleTextChange = (e) => {
    // e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  const openManageViewsPanel = () => {
    dispatch(setShowViewsPanel({}));
  };

  const setL2VisibleHandler = ({ isL2Visible }: { isL2Visible: boolean }) => {
    dispatch(
      setL2Visible({
        isL2Visible,
      })
    );
  };

  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];
  const applicableL2DirectReportsModules = getApplicableL2MyTeamModules('directReports', moduleName);
  const applicableL2AllReportsModules = getApplicableL2MyTeamModules('allReports', moduleName);
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName);

  const { handleModuleWithIntentRedirection } = useL1IntentNavigation();

  // Global search
  const onClickSearch = () => {
    sendClickedOnSearch();
    dispatch(setGlobalSearchVisible());
  };

  return (
    <LeftNavWrapper>
      <LeftNavGroup align="top" id="primary-nav-top-group" data-testid="primary-nav-top-group">
        <LeftNavItem>
          <Link to={routes.home}>
            {checkEnableCompanySupport() ? (
              <Box w="56px" h="56px" cursor="pointer" display="flex" alignItems="center" justifyContent="center">
                <CustomLogoWrapper src={CustomLogo} />
              </Box>
            ) : (
              <LeftNavHome
                onClick={() => {
                  sendClickedOnHome();
                  setL2VisibleHandler({ isL2Visible: false });
                }}
              />
            )}
          </Link>
        </LeftNavItem>
        <LeftNavItem>
          <LeftNavButton
            icon={Search}
            onClick={onClickSearch}
            style={{
              cursor: 'pointer',
              userSelect: 'none',
            }}
            id="primary-nav-search-btn"
          >
            Search
          </LeftNavButton>
        </LeftNavItem>
        <LeftNavItem>
          <Link to={routes.appHome}>
            <LeftNavButton
              href="#"
              isActive={isHome}
              icon={Home}
              onClick={() => {
                sendClickedOnHome();
                setL2VisibleHandler({ isL2Visible: false });
              }}
            >
              Home
            </LeftNavButton>
          </Link>
        </LeftNavItem>
        <LeftNavItem>
          <Link
            to={
              checkIntentLandingPagesFeatureEnabled()
                ? () => handleModuleWithIntentRedirection('myData')
                : applicableL2MyDataModules[0]?.value
            }
          >
            <LeftNavButton
              isActive={isMyData}
              icon={Person}
              onClick={() => {
                sendClickedOnMe();
                setL2VisibleHandler({ isL2Visible: true });
              }}
            >
              Me
            </LeftNavButton>
          </Link>
        </LeftNavItem>
        {l1Accesses.hasL1TeamAccess ? (
          <LeftNavItem>
            <LeftNavItemDropdown
              open={teamMenuStatus.isOpen || teamMenuStatus.isTriggerHovered || teamMenuStatus.isMenuHovered}
              onOpenChange={(isOpen) => {
                sendClickedOnTeams();
                setTeamMenuStatus((curStatus) => ({ ...curStatus, isOpen }));
                if (!isOpen) {
                  setSearchTerm('');
                }
              }}
              modal={false}
            >
              <LeftNavItemDropdownTrigger
                style={{
                  cursor: 'pointer',
                }}
                asChild
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse') {
                    sendClickedOnTeams();
                    setTeamMenuStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
                  }
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === 'mouse')
                    setTeamMenuStatus((curStatus) => ({ ...curStatus, isTriggerHovered: false }));
                  setSearchTerm('');
                }}
              >
                <LeftNavButton icon={Team} isActive={isTeam} variant="withMenu">
                  Teams
                </LeftNavButton>
              </LeftNavItemDropdownTrigger>
              <LeftNavItemDropdownContent
                side="right"
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse')
                    setTeamMenuStatus((curStatus) => ({ ...curStatus, isMenuHovered: true }));
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType === 'mouse')
                    setTeamMenuStatus((curStatus) => ({ ...curStatus, isMenuHovered: false }));
                  setSearchTerm('');
                }}
                align="start"
                style={{
                  padding: '4px',
                  position: 'relative',
                  gap: userAccesses.isDirectManager || userAccesses.isSkipManager ? '0.25rem' : '0px',
                }}
                asChild
              >
                <Box
                  // onKeyDown={(e) => {
                  //   e.preventDefault();
                  // }}
                  data-testid="l1-teams-dropdown"
                >
                  {(userAccesses.isDirectManager || userAccesses.isSkipManager) && (
                    <Flex alignItems="center" w="100%" p="4px 8px">
                      <TextLabel color="text.g2" bold>
                        TEAMS
                      </TextLabel>
                    </Flex>
                  )}
                  <LeftNavItemDropdownRadioGroup value={teamType}>
                    {userAccesses.isDirectManager ? (
                      <Link
                        to={
                          checkIntentLandingPagesFeatureEnabled()
                            ? () => handleModuleWithIntentRedirection('team', 'directReports')
                            : applicableL2DirectReportsModules[0]?.value
                        }
                      >
                        <LeftNavItemDropdownRadioItem
                          value="directReports"
                          onClick={() => {
                            sendClickedOnDirectTeam();
                            setL2VisibleHandler({ isL2Visible: true });
                          }}
                          style={{
                            marginBottom: '4px',
                          }}
                        >
                          <span>Direct Team</span>
                        </LeftNavItemDropdownRadioItem>
                      </Link>
                    ) : null}
                    {userAccesses.isSkipManager ? (
                      <Link
                        to={
                          checkIntentLandingPagesFeatureEnabled()
                            ? () => handleModuleWithIntentRedirection('team', 'allReports')
                            : applicableL2AllReportsModules[0]?.value
                        }
                      >
                        <LeftNavItemDropdownRadioItem
                          value="allReports"
                          onClick={() => {
                            sendClickedOnSkipTeam();
                            setL2VisibleHandler({ isL2Visible: true });
                          }}
                          style={{
                            marginBottom: '4px',
                          }}
                        >
                          <span>Skip Team</span>
                        </LeftNavItemDropdownRadioItem>
                      </Link>
                    ) : null}
                  </LeftNavItemDropdownRadioGroup>
                  {(userAccesses.isDirectManager || userAccesses.isSkipManager) && l1Accesses.hasViewsAccess ? (
                    <LeftNavItemDropdownSeparator />
                  ) : null}

                  {l1Accesses.hasViewsAccess ? (
                    <Box w="240px" height={cohorts.length === 0 ? 'auto' : '240px'} p="4px">
                      <Flex
                        alignItems="center"
                        w="100%"
                        p={userAccesses.isDirectManager || userAccesses.isSkipManager ? '4px 8px' : '0px 8px'}
                        sx={{
                          '.chakra-button': {
                            paddingRight: 0,
                          },
                        }}
                      >
                        <TextLabel color="text.g2" bold>
                          CUSTOM
                        </TextLabel>
                        <ButtonV1
                          StartIcon={ManageIcon}
                          ml="auto"
                          variant="tertiary"
                          sx={{
                            '.chakra-text': {
                              margin: 0,
                              marginLeft: '2px',
                            },
                          }}
                          onClick={openManageViewsPanel}
                          isDisabled={cohorts?.length === 0}
                        >
                          Manage
                        </ButtonV1>
                      </Flex>

                      {cohorts.length > 0 ? (
                        <Input
                          placeholder="Search"
                          onChange={handleTextChange}
                          value={searchTerm}
                          style={{
                            minWidth: 0,
                            marginBottom: '4px',
                          }}
                          type="search"
                        />
                      ) : null}

                      {cohorts.length > 0 ? (
                        <LeftNavItemDropdownRadioGroup
                          value={viewId}
                          style={{
                            overflow: 'auto',
                            height: '150px',
                          }}
                        >
                          {searchTerm.length
                            ? results?.map(({ item: { enabledFeatures, id, name } }) => {
                                const applicableL2ViewModules = getApplicableL2ViewModules(moduleName, enabledFeatures);

                                return (
                                  <Link
                                    to={() =>
                                      checkIntentLandingPagesFeatureEnabled()
                                        ? handleModuleWithIntentRedirection('team', 'view', {
                                            id: String(id),
                                            enabledFeatures,
                                          })
                                        : getViewRoute(applicableL2ViewModules[0]?.value, String(id))
                                    }
                                    key={`view-id-${id}`}
                                  >
                                    <LeftNavItemDropdownRadioItem
                                      value={String(id)}
                                      onClick={() => setL2VisibleHandler({ isL2Visible: true })}
                                      style={{
                                        marginBottom: '4px',
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '100%',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {name}
                                      </span>
                                    </LeftNavItemDropdownRadioItem>
                                  </Link>
                                );
                              })
                            : null}
                          {searchTerm.length === 0
                            ? cohorts?.map(({ id, name, enabledFeatures }) => {
                                const applicableL2ViewModules = getApplicableL2ViewModules(moduleName, enabledFeatures);
                                return (
                                  <Link
                                    // to={getViewRoute(applicableL2ViewModules[0]?.value, String(id))}
                                    to={() =>
                                      checkIntentLandingPagesFeatureEnabled()
                                        ? handleModuleWithIntentRedirection('team', 'view', {
                                            id: String(id),
                                            enabledFeatures,
                                          })
                                        : getViewRoute(applicableL2ViewModules[0]?.value, String(id))
                                    }
                                    key={`view-id-${id}`}
                                  >
                                    <LeftNavItemDropdownRadioItem
                                      value={String(id)}
                                      onClick={() => setL2VisibleHandler({ isL2Visible: true })}
                                      style={{
                                        marginBottom: '4px',
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '100%',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {name}
                                      </span>
                                    </LeftNavItemDropdownRadioItem>
                                  </Link>
                                );
                              })
                            : null}
                        </LeftNavItemDropdownRadioGroup>
                      ) : (
                        <EmptyViews />
                      )}
                    </Box>
                  ) : null}
                </Box>
              </LeftNavItemDropdownContent>
            </LeftNavItemDropdown>
          </LeftNavItem>
        ) : null}

        {l1Accesses.hasL1CompanyAccess ? (
          <LeftNavItem>
            <Link
              to={
                // eslint-disable-next-line no-nested-ternary
                checkIntentLandingPagesFeatureEnabled()
                  ? () => handleModuleWithIntentRedirection('company')
                  : applicableL2CompanyModules.length > 0
                    ? applicableL2CompanyModules[0]?.value
                    : staticRoutes.company.settings.platform.organization
              }
            >
              <LeftNavButton
                icon={Company}
                onClick={() => {
                  sendClickedOnCompany();
                  setL2VisibleHandler({ isL2Visible: true });
                }}
                isActive={isCompany}
                style={isCompany && { padding: '0px 32px' }}
              >
                Company
              </LeftNavButton>
            </Link>
          </LeftNavItem>
        ) : null}
      </LeftNavGroup>
      <LeftNavGroup align="bottom" id="primary-nav-bottom-group" data-testid="primary-nav-bottom-group">
        <LeftNavItem>
          <Dropdown
            open={createMenuStatus.isOpen || createMenuStatus.isMenuHovered || createMenuStatus.isTriggerHovered}
            onOpenChange={(isOpen) => {
              setCreateMenuStatus((curStatus) => ({ ...curStatus, isOpen }));
            }}
            modal={false}
            data-testid="primary-nav-add-dropdown"
          >
            <DropdownTrigger
              asChild
              onClick={() => {
                sendClickedOnQuickAdd();
                setCreateMenuStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') {
                  sendClickedOnQuickAdd();
                  setCreateMenuStatus((curStatus) => ({ ...curStatus, isTriggerHovered: true }));
                }
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse')
                  setCreateMenuStatus((curStatus) => ({ ...curStatus, isTriggerHovered: false }));
              }}
            >
              <Button
                className="transition ease-linear select-none "
                variant="nav"
                size="nav"
                iconLeft={Add}
                id="primary-nav-add-btn"
                data-testid="primary-nav-add-btn"
              >
                Add
              </Button>
            </DropdownTrigger>
            <DropdownContent
              style={{
                padding: '8px',
              }}
              align="end"
              side="right"
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse')
                  setCreateMenuStatus((curStatus) => ({ ...curStatus, isMenuHovered: true }));
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse')
                  setCreateMenuStatus((curStatus) => ({ ...curStatus, isMenuHovered: false }));
              }}
            >
              <QuickActions />
            </DropdownContent>
          </Dropdown>
        </LeftNavItem>

        {/* <LeftNavItem> */}
        {/* eslint-disable-next-line react/no-unstable-nested-components */}
        {/* <LeftNavButton
            href="#"
            // eslint-disable-next-line react/no-unstable-nested-components
            icon={() => <MeshMavenWithYellowBackground boxSize="44px" />}
            onClick={() => {
              sendClickedOnAskMaven();
              // openCopilot();
            }}
          > */}
        {/* <TextLabel
              mt="2px"
              fontSize="8px"
              fontWeight="bold"
              bgGradient="linear(90deg, #038EEF 9.05%, #ED3C65 86.39%)"
              bgClip="text"
            >
              Ask Maven
            </TextLabel>
          </LeftNavButton>
        </LeftNavItem> */}
        <LeftNavItem>
          <NotificationsProvider>
            <NotificationsWrapper>
              <ProfileMenu />
            </NotificationsWrapper>
          </NotificationsProvider>
        </LeftNavItem>
      </LeftNavGroup>
    </LeftNavWrapper>
  );
};
